/**
 * AnalogInputsPanel - Configure sliders and pedal MIDI settings
 * with live visualization and calibration controls
 * Spec §5.18: Analog Inputs (v1.4)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useConfigStore } from '@/store/configStore';
import { getSerialConnection, parseJsonFromCliResponse } from '@/lib/serialConnection';
import { useAnalogLive } from '@/hooks/useAnalogLive';
import type { SliderMidiType, PedalDetectMode, AnalogConfig } from '@/types/config';

// Default config when analog inputs are first enabled
const DEFAULT_ANALOG_CONFIG: AnalogConfig = {
  sliders: [
    { midi_type: 'cc', channel: 0, cc: 1, min: 0, max: 127, invert: false, spring_center: false },
    { midi_type: 'cc', channel: 0, cc: 2, min: 0, max: 127, invert: false, spring_center: false },
    { midi_type: 'cc', channel: 0, cc: 3, min: 0, max: 127, invert: false, spring_center: false },
  ],
  pedal: {
    detect_mode: 'auto',
    sustain: { cc: 64, channel: 0, invert: false },
    expression: { cc: 11, channel: 0, min: 0, max: 127, invert: false },
  },
};

const SLIDER_MIDI_TYPES: { value: SliderMidiType; label: string }[] = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'cc', label: 'Control Change (CC)' },
  { value: 'pitch_bend', label: 'Pitch Bend' },
  { value: 'aftertouch', label: 'Channel Aftertouch' },
];

const PEDAL_DETECT_MODES: { value: PedalDetectMode; label: string }[] = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'sustain', label: 'Force Sustain (TS)' },
  { value: 'expression', label: 'Force Expression (TRS)' },
];

/* ============================================================================
 * CALIBRATION TYPES
 * ============================================================================ */

interface CalChannel {
  valid: boolean;
  min?: number;
  max?: number;
  dz?: number;
}

interface CalStatus {
  active: boolean;
  channel: number;
  channels: CalChannel[];
}

/* ============================================================================
 * VALUE BAR COMPONENT
 * ============================================================================ */

function ValueBar({
  value,
  springCenter,
  label,
  noData,
}: {
  value: number;
  springCenter?: boolean;
  label?: string;
  noData?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, (value / 127) * 100));
  const dimmed = noData || value < 0;

  if (springCenter) {
    // Center-origin bar: fills from center (50%) outward
    const centerPct = 50;
    const valuePct = pct;
    const barLeft = Math.min(centerPct, valuePct);
    const barWidth = Math.abs(valuePct - centerPct);

    return (
      <div style={{ marginBottom: '8px' }}>
        {label && (
          <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>
            {label}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              flex: 1,
              height: '14px',
              backgroundColor: dimmed ? '#e9ecef' : '#f0f0f0',
              borderRadius: '3px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Center marker */}
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: '1px',
                backgroundColor: '#999',
                zIndex: 1,
              }}
            />
            {!dimmed && (
              <div
                style={{
                  position: 'absolute',
                  left: `${barLeft}%`,
                  width: `${barWidth}%`,
                  top: 0,
                  bottom: 0,
                  backgroundColor: '#e8820e',
                  borderRadius: '2px',
                  transition: 'left 0.05s, width 0.05s',
                }}
              />
            )}
          </div>
          <span
            style={{
              fontSize: '12px',
              fontFamily: 'monospace',
              color: dimmed ? '#ccc' : '#333',
              minWidth: '40px',
              textAlign: 'right',
            }}
          >
            {dimmed ? '---' : value}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '8px' }}>
      {label && (
        <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>
          {label}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            flex: 1,
            height: '14px',
            backgroundColor: dimmed ? '#e9ecef' : '#f0f0f0',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          {!dimmed && (
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                backgroundColor: '#4a90d9',
                borderRadius: '3px',
                transition: 'width 0.05s',
              }}
            />
          )}
        </div>
        <span
          style={{
            fontSize: '12px',
            fontFamily: 'monospace',
            color: dimmed ? '#ccc' : '#333',
            minWidth: '40px',
            textAlign: 'right',
          }}
        >
          {dimmed ? '---' : value}
        </span>
      </div>
    </div>
  );
}

/* ============================================================================
 * CALIBRATION SECTION COMPONENT
 * ============================================================================ */

function CalibrationSection({
  channelIndex,
  channelLabel,
  calStatus,
  onStart,
  onStop,
  onAbort,
}: {
  channelIndex: number;
  channelLabel: string;
  calStatus: CalStatus | null;
  onStart: () => void;
  onStop: () => void;
  onAbort: () => void;
}) {
  const ch = calStatus?.channels?.[channelIndex];
  const isThisActive = calStatus?.active && calStatus.channel === channelIndex;
  const anyActive = calStatus?.active;

  return (
    <div
      style={{
        padding: '8px',
        marginTop: '8px',
        backgroundColor: '#f4f6f8',
        borderRadius: '4px',
        border: isThisActive ? '1px solid #e8820e' : '1px solid #e0e0e0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '12px' }}>
          {ch?.valid ? (
            <span style={{ color: '#28a745' }}>
              Calibrated (min={ch.min} max={ch.max} dz={ch.dz}%)
            </span>
          ) : (
            <span style={{ color: '#6c757d' }}>Not calibrated (full range)</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {isThisActive ? (
            <>
              <button
                onClick={onStop}
                style={{
                  padding: '3px 10px',
                  fontSize: '12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                onClick={onAbort}
                style={{
                  padding: '3px 10px',
                  fontSize: '12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                }}
              >
                Abort
              </button>
            </>
          ) : (
            <button
              onClick={onStart}
              disabled={!!anyActive}
              style={{
                padding: '3px 10px',
                fontSize: '12px',
                backgroundColor: anyActive ? '#ccc' : '#4a90d9',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: anyActive ? 'not-allowed' : 'pointer',
              }}
            >
              Calibrate {channelLabel}
            </button>
          )}
        </div>
      </div>
      {isThisActive && (
        <div
          style={{
            marginTop: '6px',
            fontSize: '12px',
            color: '#e8820e',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#e8820e',
              animation: 'pulse 1s infinite',
            }}
          />
          Move {channelLabel.toLowerCase()} to both extremes...
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * SLIDER CONFIG SECTION
 * ============================================================================ */

interface SliderConfigProps {
  index: number;
  config: AnalogConfig['sliders'][0];
  onChange: (config: Partial<AnalogConfig['sliders'][0]>) => void;
  liveValue: number;
  calStatus: CalStatus | null;
  onCalStart: (ch: number) => void;
  onCalStop: () => void;
  onCalAbort: () => void;
}

function SliderConfigSection({
  index,
  config,
  onChange,
  liveValue,
  calStatus,
  onCalStart,
  onCalStop,
  onCalAbort,
}: SliderConfigProps) {
  return (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#f9f9f9',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
      }}
    >
      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
        Slider {index + 1}{config.spring_center ? ' (Spring Wheel)' : ''}
      </h4>

      {/* Live value bar */}
      <ValueBar
        value={liveValue}
        springCenter={config.spring_center}
        noData={liveValue < 0}
      />

      {/* MIDI Type */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
          MIDI Type:
        </label>
        <select
          value={config.midi_type}
          onChange={(e) => onChange({ midi_type: e.target.value as SliderMidiType })}
          style={{
            width: '100%',
            padding: '6px',
            fontSize: '13px',
            border: '1px solid #ccc',
            borderRadius: '4px',
          }}
        >
          {SLIDER_MIDI_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {config.midi_type !== 'disabled' && (
        <>
          {/* Channel */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
              MIDI Channel (0-15):
            </label>
            <input
              type="number"
              min={0}
              max={15}
              value={config.channel}
              onChange={(e) =>
                onChange({ channel: Math.max(0, Math.min(15, parseInt(e.target.value) || 0)) })
              }
              style={{ width: '60px', padding: '4px', fontSize: '13px' }}
            />
          </div>

          {/* CC Number (only for CC type) */}
          {config.midi_type === 'cc' && (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                CC Number (0-127):
              </label>
              <input
                type="number"
                min={0}
                max={127}
                value={config.cc}
                onChange={(e) =>
                  onChange({ cc: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)) })
                }
                style={{ width: '60px', padding: '4px', fontSize: '13px' }}
              />
            </div>
          )}

          {/* Min/Max Range */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
              Output Range (0-127):
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                min={0}
                max={127}
                value={config.min}
                onChange={(e) =>
                  onChange({ min: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)) })
                }
                style={{ width: '60px', padding: '4px', fontSize: '13px' }}
              />
              <span style={{ fontSize: '13px' }}>to</span>
              <input
                type="number"
                min={0}
                max={127}
                value={config.max}
                onChange={(e) =>
                  onChange({ max: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)) })
                }
                style={{ width: '60px', padding: '4px', fontSize: '13px' }}
              />
            </div>
          </div>

          {/* Invert */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              cursor: 'pointer',
              marginBottom: '8px',
            }}
          >
            <input
              type="checkbox"
              checked={config.invert}
              onChange={(e) => onChange({ invert: e.target.checked })}
            />
            Invert direction
          </label>

          {/* Spring wheel mode */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={config.spring_center}
              onChange={(e) => onChange({ spring_center: e.target.checked })}
            />
            Spring wheel (center rest)
          </label>
          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px', marginLeft: '24px' }}>
            Enable for spring-return wheels where center position is the rest state
          </div>
        </>
      )}

      {/* Calibration */}
      <CalibrationSection
        channelIndex={index}
        channelLabel={`Slider ${index + 1}`}
        calStatus={calStatus}
        onStart={() => onCalStart(index)}
        onStop={onCalStop}
        onAbort={onCalAbort}
      />
    </div>
  );
}

/* ============================================================================
 * PEDAL CONFIG SECTION
 * ============================================================================ */

function PedalConfigSection({
  config,
  onDetectModeChange,
  onSustainChange,
  onExpressionChange,
  pedalLive,
  calStatus,
  onCalStart,
  onCalStop,
  onCalAbort,
}: {
  config: AnalogConfig['pedal'];
  onDetectModeChange: (mode: PedalDetectMode) => void;
  onSustainChange: (config: Partial<AnalogConfig['pedal']['sustain']>) => void;
  onExpressionChange: (config: Partial<AnalogConfig['pedal']['expression']>) => void;
  pedalLive: { type: 'none' | 'sustain' | 'expression'; value: number };
  calStatus: CalStatus | null;
  onCalStart: () => void;
  onCalStop: () => void;
  onCalAbort: () => void;
}) {
  return (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#f9f9f9',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
      }}
    >
      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
        Pedal Jack
      </h4>

      {/* Live pedal indicator */}
      <div style={{ marginBottom: '10px' }}>
        {pedalLive.type === 'none' && (
          <div style={{ fontSize: '12px', color: '#999' }}>No pedal detected</div>
        )}
        {pedalLive.type === 'sustain' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: pedalLive.value > 0 ? '#28a745' : '#ccc',
                border: '1px solid #999',
              }}
            />
            <span>Sustain: {pedalLive.value > 0 ? 'ON' : 'OFF'}</span>
          </div>
        )}
        {pedalLive.type === 'expression' && (
          <ValueBar value={pedalLive.value} label="Expression" />
        )}
      </div>

      {/* Detection Mode */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
          Detection Mode:
        </label>
        <select
          value={config.detect_mode}
          onChange={(e) => onDetectModeChange(e.target.value as PedalDetectMode)}
          style={{
            width: '100%',
            padding: '6px',
            fontSize: '13px',
            border: '1px solid #ccc',
            borderRadius: '4px',
          }}
        >
          {PEDAL_DETECT_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </select>
        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
          Auto: Detects TS (sustain) vs TRS (expression) pedal automatically
        </div>
      </div>

      {/* Sustain Pedal Settings */}
      <div
        style={{
          padding: '10px',
          marginBottom: '12px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
        }}
      >
        <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 'bold' }}>
          Sustain (TS Pedal)
        </h5>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
              CC Number:
            </label>
            <input
              type="number"
              min={0}
              max={127}
              value={config.sustain.cc}
              onChange={(e) =>
                onSustainChange({ cc: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)) })
              }
              style={{ width: '60px', padding: '4px', fontSize: '12px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
              Channel:
            </label>
            <input
              type="number"
              min={0}
              max={15}
              value={config.sustain.channel}
              onChange={(e) =>
                onSustainChange({
                  channel: Math.max(0, Math.min(15, parseInt(e.target.value) || 0)),
                })
              }
              style={{ width: '60px', padding: '4px', fontSize: '12px' }}
            />
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={config.sustain.invert}
            onChange={(e) => onSustainChange({ invert: e.target.checked })}
          />
          Invert (swap on/off)
        </label>
      </div>

      {/* Expression Pedal Settings */}
      <div
        style={{
          padding: '10px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
        }}
      >
        <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 'bold' }}>
          Expression (TRS Pedal)
        </h5>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
              CC Number:
            </label>
            <input
              type="number"
              min={0}
              max={127}
              value={config.expression.cc}
              onChange={(e) =>
                onExpressionChange({
                  cc: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)),
                })
              }
              style={{ width: '60px', padding: '4px', fontSize: '12px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
              Channel:
            </label>
            <input
              type="number"
              min={0}
              max={15}
              value={config.expression.channel}
              onChange={(e) =>
                onExpressionChange({
                  channel: Math.max(0, Math.min(15, parseInt(e.target.value) || 0)),
                })
              }
              style={{ width: '60px', padding: '4px', fontSize: '12px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
            Output Range:
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="number"
              min={0}
              max={127}
              value={config.expression.min}
              onChange={(e) =>
                onExpressionChange({
                  min: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)),
                })
              }
              style={{ width: '60px', padding: '4px', fontSize: '12px' }}
            />
            <span style={{ fontSize: '12px' }}>to</span>
            <input
              type="number"
              min={0}
              max={127}
              value={config.expression.max}
              onChange={(e) =>
                onExpressionChange({
                  max: Math.max(0, Math.min(127, parseInt(e.target.value) || 0)),
                })
              }
              style={{ width: '60px', padding: '4px', fontSize: '12px' }}
            />
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={config.expression.invert}
            onChange={(e) => onExpressionChange({ invert: e.target.checked })}
          />
          Invert direction
        </label>
      </div>

      {/* Expression pedal calibration */}
      <CalibrationSection
        channelIndex={3}
        channelLabel="Expression Pedal"
        calStatus={calStatus}
        onStart={onCalStart}
        onStop={onCalStop}
        onAbort={onCalAbort}
      />
    </div>
  );
}

/* ============================================================================
 * MAIN PANEL
 * ============================================================================ */

export function AnalogInputsPanel() {
  const analogConfig = useConfigStore((state) => state.analogConfig);
  const setAnalogConfig = useConfigStore((state) => state.setAnalogConfig);
  const updateSliderConfig = useConfigStore((state) => state.updateSliderConfig);
  const updatePedalConfig = useConfigStore((state) => state.updatePedalConfig);
  const liveVizEnabled = useConfigStore((state) => state.liveVisualizationEnabled);

  const analogLive = useAnalogLive(liveVizEnabled);

  const [calStatus, setCalStatus] = useState<CalStatus | null>(null);
  const [calMessage, setCalMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const pollingRef = useRef<number | null>(null);

  const isEnabled = analogConfig !== undefined;
  const config = analogConfig ?? DEFAULT_ANALOG_CONFIG;

  // Fetch calibration status
  const fetchCalStatus = useCallback(async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) return;
    const response = await conn.sendCommand('json analogcal');
    const data = parseJsonFromCliResponse(response.raw) as CalStatus | null;
    if (data) setCalStatus(data);
  }, []);

  // Poll during active calibration
  useEffect(() => {
    if (calStatus?.active) {
      pollingRef.current = window.setInterval(fetchCalStatus, 500);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [calStatus?.active, fetchCalStatus]);

  // Fetch on mount / connection
  useEffect(() => {
    const conn = getSerialConnection();
    const unsub = conn.onStateChange((state: string) => {
      if (state === 'connected') {
        setTimeout(fetchCalStatus, 800);
      }
    });
    if (conn.isConnected()) fetchCalStatus();
    return unsub;
  }, [fetchCalStatus]);

  const handleCalStart = useCallback(async (channel: number) => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) return;
    setCalMessage(null);
    const cmd = channel === 3 ? 'analog cal pedal' : `analog cal ${channel + 1}`;
    const response = await conn.sendCommand(cmd);
    if (response.raw.includes('ERR:')) {
      setCalMessage({ type: 'error', text: response.raw.trim() });
    } else {
      await fetchCalStatus();
    }
  }, [fetchCalStatus]);

  const handleCalStop = useCallback(async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) return;
    setCalMessage(null);
    const response = await conn.sendCommand('analog cal stop');
    if (response.raw.includes('OK:')) {
      setCalMessage({ type: 'success', text: 'Calibration saved' });
    } else if (response.raw.includes('ERR:')) {
      setCalMessage({ type: 'error', text: response.raw.trim() });
    }
    await fetchCalStatus();
  }, [fetchCalStatus]);

  const handleCalAbort = useCallback(async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) return;
    setCalMessage(null);
    await conn.sendCommand('analog cal abort');
    setCalMessage({ type: 'success', text: 'Calibration aborted' });
    await fetchCalStatus();
  }, [fetchCalStatus]);

  const handleCalReset = useCallback(async () => {
    if (!window.confirm('Reset all analog calibration data? This cannot be undone.')) return;
    const conn = getSerialConnection();
    if (!conn.isConnected()) return;
    setCalMessage(null);
    const response = await conn.sendCommand('analog cal reset');
    if (response.raw.includes('OK:')) {
      setCalMessage({ type: 'success', text: 'All calibration reset' });
    } else {
      setCalMessage({ type: 'error', text: response.raw.trim() });
    }
    await fetchCalStatus();
  }, [fetchCalStatus]);

  const handleToggleEnabled = () => {
    if (isEnabled) {
      setAnalogConfig(undefined);
    } else {
      setAnalogConfig(DEFAULT_ANALOG_CONFIG);
    }
  };

  return (
    <>
      {/* Pulse animation for calibration indicator */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      {/* Enable/Disable toggle */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={handleToggleEnabled}
            style={{ width: '18px', height: '18px' }}
          />
          Enable Analog Inputs Configuration
        </label>
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', marginLeft: '26px' }}>
          Configure how sliders and pedal generate MIDI messages
        </div>
      </div>

      {isEnabled && (
        <>
          {/* Calibration message */}
          {calMessage && (
            <div
              style={{
                padding: '8px 12px',
                marginBottom: '12px',
                backgroundColor: calMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                color: calMessage.type === 'success' ? '#155724' : '#721c24',
                borderRadius: '4px',
                fontSize: '13px',
              }}
            >
              {calMessage.text}
            </div>
          )}

          {/* Sliders Section */}
          <h3 style={{ fontSize: '15px', marginBottom: '12px', borderBottom: '1px solid #e9ecef', paddingBottom: '4px' }}>
            Sliders
          </h3>
          {([0, 1, 2] as const).map((index) => (
            <SliderConfigSection
              key={index}
              index={index}
              config={config.sliders[index]}
              onChange={(cfg) => updateSliderConfig(index, cfg)}
              liveValue={analogLive.sliders[index]}
              calStatus={calStatus}
              onCalStart={handleCalStart}
              onCalStop={handleCalStop}
              onCalAbort={handleCalAbort}
            />
          ))}

          {/* Pedal Section */}
          <h3 style={{ fontSize: '15px', marginBottom: '12px', borderBottom: '1px solid #e9ecef', paddingBottom: '4px' }}>
            Pedal
          </h3>
          <PedalConfigSection
            config={config.pedal}
            onDetectModeChange={(mode) => updatePedalConfig({ detect_mode: mode })}
            onSustainChange={(cfg) =>
              updatePedalConfig({ sustain: { ...config.pedal.sustain, ...cfg } })
            }
            onExpressionChange={(cfg) =>
              updatePedalConfig({ expression: { ...config.pedal.expression, ...cfg } })
            }
            pedalLive={analogLive.pedal}
            calStatus={calStatus}
            onCalStart={() => handleCalStart(3)}
            onCalStop={handleCalStop}
            onCalAbort={handleCalAbort}
          />

          {/* Reset All Calibration */}
          <div style={{ marginTop: '12px', marginBottom: '12px' }}>
            <button
              onClick={handleCalReset}
              disabled={!!calStatus?.active}
              style={{
                padding: '6px 14px',
                fontSize: '12px',
                backgroundColor: calStatus?.active ? '#ccc' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: calStatus?.active ? 'not-allowed' : 'pointer',
              }}
            >
              Reset All Calibration
            </button>
          </div>

          {/* Info box */}
          <div
            style={{
              padding: '8px',
              backgroundColor: '#e7f3ff',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#0056b3',
            }}
          >
            <strong>Sliders:</strong> 3 linear potentiometers on the Master Module.
            <br />
            <strong>Pedal Jack:</strong> 1/4" TRS jack supporting sustain (TS) or expression (TRS) pedals.
            <br />
            <strong>Auto-detect:</strong> Firmware detects pedal type based on RING signal level.
            <br />
            <strong>Calibration:</strong> Move slider/pedal to both physical extremes, then save.
          </div>
        </>
      )}

      {!isEnabled && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#6c757d',
            textAlign: 'center',
          }}
        >
          Analog inputs configuration is disabled.
          <br />
          Default MIDI mappings will be used (CC 1-3 for sliders, CC 64/11 for pedal).
        </div>
      )}
    </>
  );
}
